import { useCallback, useEffect, useMemo, useState } from 'react'

import classNames from 'classnames'
import { debounce } from 'lodash'
import './EntitySearchHeader.scss'
import { useTranslation } from 'react-i18next'
import { connect } from 'scrivito'

import { Icon } from '@/Atomic/Atoms/Icon'
import { Button } from '@/Atomic/Molecules/Button'
import { MenuItemProps } from '@/Atomic/Molecules/MenuItem'
import { SavedFilterStructure } from '@/Atomic/Organisms/FilterBuilder'
import {
  FilterList,
  FilterItem,
  CURRENT_FILTER_ID,
} from '@/Atomic/Organisms/FilterList'
import { MenuSheet } from '@/Atomic/Organisms/MenuSheet'
import { SaveAsFilterModal } from '@/Atomic/Widgets/AdvancedSearchModal/SaveAsFilterModal'
import { EditFilterModal } from '@/Atomic/Widgets/EditFilterModal'
import { isDataClassName } from '@/Data/const'
import { useActiveFilter } from '@/hooks/useActiveFilter'
import { useIconMenu } from '@/hooks/useIconMenu'
import { invalidateViaRegistry } from '@/hooks/useInvalidationRegistry'
import { useMediaQuery } from '@/hooks/useMediaQuery'
import { generateUniqueName, SavedFilter } from '@/hooks/useSavedFilters'
import { useSmartPosition } from '@/hooks/useSmartPosition'
import { FilterUsageStorage } from '@/utils/filterUsageStorage'
import { sortFilters, filterBySearch } from '@/utils/sortFilters'

interface ListFilterProps {
  entityName: string
  /** Opens the advanced-filter builder. `seeded` pre-fills it from the URL. */
  onAdvancedFilterClick?: (mode?: 'blank' | 'seeded') => void
  group?: string // Shared icon-menu group for mutual exclusion with sibling dropdowns
}

function ListFilterComponent({
  entityName,
  onAdvancedFilterClick,
  group,
}: ListFilterProps) {
  const { t } = useTranslation()
  const isMobile = useMediaQuery('(max-width: 766px)')

  const { buttonRef, menuIsOpen, toggleMenu, setMenuIsOpen } = useIconMenu({
    group: group ?? `filter-list-${entityName}`,
  })

  // Use smart position for the filter list panel
  const { panelRef, position, hasMeasured } = useSmartPosition({
    isOpen: menuIsOpen,
    triggerRef: buttonRef,
    preferHorizontal: 'left',
    preferVertical: 'bottom',
    listenToScroll: true,
  })

  const [searchKey, setSearchKey] = useState<string>('')

  // Reset search key when menu closes
  useEffect(() => {
    if (!menuIsOpen) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setSearchKey('')
    }
  }, [menuIsOpen])

  // Advanced Search modal is enabled if onAdvancedFilterClick is provided
  const isAdvancedSearchModalEnabled = !!onAdvancedFilterClick

  // Selection, the unsaved draft and the saved-filter store all come from the
  // URL + Filter DataClass. Nothing about the filter itself is component state.
  const {
    selectedFilterId,
    draft,
    activeSource,
    savedFilters,
    allFilters,
    createFilter,
    updateFilter,
    deleteFilter,
    getFilter,
    toggleFavorite,
    selectFilter,
    selectDraft,
    applyDraft,
    discardDraft,
    selectSavedFilter,
    clearAdvancedFilter,
  } = useActiveFilter(entityName)

  // Advanced search modal state
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [editingFilter, setEditingFilter] = useState<SavedFilter | null>(null)

  // SaveAsFilter modal state for current filter
  const [isSaveAsModalOpen, setIsSaveAsModalOpen] = useState(false)

  // Mobile menu state
  const [mobileMenuFilter, setMobileMenuFilter] = useState<FilterItem | null>(
    null,
  )
  const [mobileMenuItems, setMobileMenuItems] = useState<MenuItemProps[]>([])

  // Prepare filter items for the FilterList component
  const filterItems: FilterItem[] = useMemo(() => {
    // localStorage is not reactive, so read it here rather than once-memoized on
    // `entityName`: `selectedFilterId` is a dep, and selecting a filter is
    // exactly when its usage timestamp is written, so the recency sort refreshes
    // without a reload.
    const filterUsage = FilterUsageStorage.load(entityName)

    // Map filters to FilterItem format with usage data
    const withUsage = allFilters.map((f) => ({
      id: f.id,
      label: f.title,
      isFavorite: f.favorite,
      isUserCreated: f.kind === 'neocrm',
      lastUsed: filterUsage[f.id],
    }))

    // Filter by search term
    const searched = filterBySearch(withUsage, searchKey)

    // Sort filters with priority rules
    const sorted = sortFilters(searched, selectedFilterId)

    // Add "Current filter" at the top if there's an unsaved advanced search filter
    if (draft) {
      sorted.unshift({
        id: CURRENT_FILTER_ID,
        label: t('advancedSearch.currentFilter'),
        isFavorite: false,
        isUserCreated: false,
      })
    }

    return sorted
  }, [allFilters, selectedFilterId, searchKey, draft, t, entityName])

  const search = useCallback((input: string) => {
    setSearchKey(input)
  }, [])

  const debouncedSearch = useMemo(
    () => debounce((value) => search(value), 500),
    [search],
  )

  const handleSelectFilter = useCallback(
    (id: string) => {
      // Selecting the unsaved row just deselects the saved filter — the draft is
      // still in the URL, so it takes over as the active filter.
      if (id === CURRENT_FILTER_ID) {
        selectDraft()
      } else {
        selectFilter(id)
      }
      setMenuIsOpen(false)
    },
    [selectDraft, selectFilter, setMenuIsOpen],
  )

  const handleToggleFavorite = useCallback(
    (id: string) => {
      toggleFavorite(id)
    },
    [toggleFavorite],
  )

  const handleEditFilter = useCallback(
    (item: FilterItem) => {
      if (!item.isUserCreated) return
      const savedFilter = getFilter(item.id)
      if (savedFilter) {
        // Close both the filter list popup and action menu on mobile when opening the edit modal
        if (isMobile) {
          setMenuIsOpen(false)
          setMobileMenuFilter(null)
        }
        setEditingFilter(savedFilter)
        setIsModalOpen(true)
      }
    },
    [getFilter, isMobile, setMenuIsOpen],
  )

  const handleDuplicateFilter = useCallback(
    (item: FilterItem) => {
      if (!item.isUserCreated) return
      const original = getFilter(item.id)
      if (original) {
        // Generate a suggested name for the duplicate
        const existingNames = savedFilters.map((f) => f.title)
        const candidateName = generateUniqueName(original.title, existingNames)

        // Close both the filter list popup and action menu on mobile when opening the edit modal
        if (isMobile) {
          setMenuIsOpen(false)
          setMobileMenuFilter(null)
        }

        // Open modal with duplicated data but no ID (treated as new filter)
        const clonedFilter = JSON.parse(JSON.stringify(original.filter))
        setEditingFilter({
          ...original,
          id: '', // Empty ID means it will be created as new on save
          title: candidateName,
          filter: clonedFilter,
        })
        setIsModalOpen(true)
      }
    },
    [getFilter, isMobile, savedFilters, setMenuIsOpen],
  )

  const handleDeleteFilter = useCallback(
    (item: FilterItem) => {
      if (!item.isUserCreated) return
      deleteFilter(item.id)
      // If the deleted filter was selected, clear selection
      if (selectedFilterId === item.id) {
        selectFilter('')
      }
    },
    [deleteFilter, selectedFilterId, selectFilter],
  )

  const handleClearAllFilters = useCallback(() => {
    clearAdvancedFilter()
    setMenuIsOpen(false)
  }, [clearAdvancedFilter, setMenuIsOpen])

  const handleSaveCurrentFilter = useCallback(() => {
    setIsSaveAsModalOpen(true)
  }, [])

  const handleSaveAsModalClose = () => {
    setIsSaveAsModalOpen(false)
  }

  const handleSaveAsCurrentFilter = async (filterName: string) => {
    if (draft) {
      const newFilter = await createFilter(filterName, draft)
      // The draft was promoted to a named filter, so `?query=` goes away.
      selectSavedFilter(newFilter.id)
    }
    setIsSaveAsModalOpen(false)
    setMenuIsOpen(false)
  }

  const handleMobileMenuOpen = useCallback(
    (filter: FilterItem, menuItems: MenuItemProps[]) => {
      setMobileMenuFilter(filter)
      setMobileMenuItems(menuItems)
    },
    [],
  )

  const handleMobileMenuClose = useCallback(() => {
    setMobileMenuFilter(null)
    setMobileMenuItems([])
  }, [])

  const handleModalClose = () => {
    setIsModalOpen(false)
    setEditingFilter(null)
  }

  const handleSaveFilter = async (
    filterName: string,
    filterData: SavedFilterStructure,
  ) => {
    const isUpdate = !!(editingFilter && editingFilter.id)
    if (isUpdate) {
      // Update existing filter with new name and data
      await updateFilter(editingFilter.id, {
        title: filterName,
        filter: filterData,
      })

      setMenuIsOpen(false)
      handleModalClose()

      // Select the just-updated filter (drops any draft; no-op URL write when it
      // was already selected). The list queries the entity class by opaque filter
      // id — editing the filter's rules changes neither the id nor the entity
      // class's cache, so force a re-fetch of that class to pick up the new query.
      selectSavedFilter(editingFilter.id)
      if (isDataClassName(entityName)) {
        invalidateViaRegistry(entityName)
      }
      return
    }

    // Create new filter (or duplicate with empty id), then select it
    const newFilter = await createFilter(filterName, filterData)
    selectSavedFilter(newFilter.id)

    setMenuIsOpen(false)
    handleModalClose()
  }

  const handleSaveAsFilterWrapper = async (
    filterName: string,
    filterData: SavedFilterStructure,
  ) => {
    const newFilter = await createFilter(filterName, filterData)
    selectSavedFilter(newFilter.id)

    // Close both modals and dropdown menu since we've saved and applied the filter
    setMenuIsOpen(false)
    handleModalClose()
  }

  const btn_testid = `filterlistbtn-${entityName}`

  return (
    <>
      <div
        className={classNames(
          'position-relative',
          isMobile ? 'd-block' : 'd-inline-block',
        )}
        ref={buttonRef}
      >
        <Button
          variant="outlined"
          size="sm"
          onClick={toggleMenu}
          data-testid={btn_testid}
          buttonClassName={isMobile ? 'mobile-filter-button' : 'filter-button'}
          aria-expanded={menuIsOpen}
          aria-haspopup="true"
          aria-controls="filter-list-panel"
          aria-label={
            isAdvancedSearchModalEnabled
              ? t('advancedSearch.title', 'Advanced filters')
              : t('common.filter.title', 'Filter')
          }
        >
          {isAdvancedSearchModalEnabled ? (
            <>
              <Icon icon="filter-horizontal" size="md" />
              <span>{`${t('advancedSearch.title')}${activeSource !== 'none' ? '\u00A0(1)' : ''}`}</span>
              <Icon icon="chevron-down" size="md" />
            </>
          ) : (
            <>
              <span>{`${t('common.filter.title')}${activeSource !== 'none' ? '\u00A0(1)' : ''}`}</span>
              <Icon icon="filter" />
            </>
          )}
        </Button>

        {/* Desktop: Dropdown panel */}
        {!isMobile && menuIsOpen && (
          <div
            id="filter-list-panel"
            ref={panelRef}
            className={classNames(
              'o-dropdown-panel',
              'filter-list-panel',
              `pos-${position}`,
            )}
            style={{
              visibility: hasMeasured ? 'visible' : 'hidden',
              width: '480px',
              zIndex: 1000,
            }}
            role="dialog"
            aria-label={t('advancedSearch.title')}
          >
            <FilterList
              filters={filterItems}
              selectedFilterId={selectedFilterId}
              onSelectFilter={handleSelectFilter}
              onSearchChange={debouncedSearch}
              onEditFilter={handleEditFilter}
              onDuplicateFilter={handleDuplicateFilter}
              onDeleteFilter={handleDeleteFilter}
              onToggleFavorite={handleToggleFavorite}
              onAdvancedFilter={
                onAdvancedFilterClick
                  ? () => onAdvancedFilterClick('blank')
                  : undefined
              }
              onEditCurrentFilter={
                onAdvancedFilterClick
                  ? () => onAdvancedFilterClick('seeded')
                  : undefined
              }
              onSaveCurrentFilter={
                onAdvancedFilterClick ? handleSaveCurrentFilter : undefined
              }
              onDeleteCurrentFilter={discardDraft}
              onClearAllFilters={handleClearAllFilters}
            />
          </div>
        )}
      </div>

      {/* Mobile: MenuSheet for filter list */}
      {isMobile && !mobileMenuFilter && menuIsOpen && (
        <MenuSheet
          isOpen={true}
          position="bottom-left"
          onBackdropClick={() => setMenuIsOpen(false)}
          onMobileCloseClick={() => setMenuIsOpen(false)}
          panelClassName="mobile-advanced-filters-list-sheet"
        >
          <div className="mobile-advanced-filters-list-content">
            <FilterList
              filters={filterItems}
              selectedFilterId={selectedFilterId}
              onSelectFilter={handleSelectFilter}
              onSearchChange={debouncedSearch}
              onEditFilter={handleEditFilter}
              onDuplicateFilter={handleDuplicateFilter}
              onDeleteFilter={handleDeleteFilter}
              onToggleFavorite={handleToggleFavorite}
              onAdvancedFilter={
                onAdvancedFilterClick
                  ? () => {
                      onAdvancedFilterClick('blank')
                      setMenuIsOpen(false)
                    }
                  : undefined
              }
              onEditCurrentFilter={
                onAdvancedFilterClick
                  ? () => {
                      onAdvancedFilterClick('seeded')
                      setMenuIsOpen(false)
                    }
                  : undefined
              }
              onSaveCurrentFilter={
                onAdvancedFilterClick ? handleSaveCurrentFilter : undefined
              }
              onDeleteCurrentFilter={discardDraft}
              onClearAllFilters={handleClearAllFilters}
              onMobileMenuOpen={handleMobileMenuOpen}
            />
          </div>
        </MenuSheet>
      )}

      {/* Mobile: MenuSheet for action menu (Edit, Duplicate, Delete) */}
      {isMobile && mobileMenuFilter && (
        <MenuSheet
          isOpen={true}
          position="bottom-left"
          onBackdropClick={handleMobileMenuClose}
          onMobileCloseClick={handleMobileMenuClose}
          panelClassName="mobile-filter-action-menu-sheet"
        >
          <div className="mobile-filter-action-menu-content">
            <div className="mobile-filter-action-menu-list">
              {mobileMenuItems.map((item, index) => (
                <button
                  key={index}
                  className={classNames('mobile-filter-action-menu-item', {
                    'is-danger': item.type === 'danger',
                  })}
                  onClick={(e) => {
                    handleMobileMenuClose()
                    item.onClick?.(e)
                  }}
                >
                  {item.icon && <Icon icon={item.icon} size="md" />}
                  <span>{item.label}</span>
                </button>
              ))}
            </div>
          </div>
        </MenuSheet>
      )}

      <EditFilterModal
        isOpen={isModalOpen}
        onClose={handleModalClose}
        onSave={handleSaveFilter}
        onSearch={applyDraft}
        onSaveAsFilter={handleSaveAsFilterWrapper}
        onClearFilter={clearAdvancedFilter}
        initialFilter={editingFilter?.filter}
        initialFilterName={editingFilter?.title}
        entityName={entityName}
      />

      <SaveAsFilterModal
        isOpen={isSaveAsModalOpen}
        onClose={handleSaveAsModalClose}
        onSave={handleSaveAsCurrentFilter}
        title={t('advancedSearch.saveFilter')}
      />
    </>
  )
}

ListFilterComponent.displayName = 'ListFilterComponent'

export const ListFilter = connect(ListFilterComponent)
